import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { useShell } from '../provider';
import type { NotificationGroup } from '../types';
import {
  getNotificationSettings,
  getNotificationUnreadCount,
  getNotifications,
  markNotificationsRead,
  markNotificationsUnread,
  readNotificationUnreadCount,
  updateNotificationSetting,
} from './notification-api';
import {
  getNotificationItems,
  normalizeNotification,
} from './notification-normalize';
import type {
  HeaderNotificationItem,
  HeaderNotificationSource,
  NotificationPreference,
} from './notification-types';
import { useNotificationStream } from './use-notification-stream';
import { createNotificationUpdateScheduler } from './notification-update-scheduler';
import { mergeNotificationRefresh } from './notification-refresh';

const NOTIFICATIONS_PAGE_SIZE = 20;

type NotificationsState = {
  items: HeaderNotificationItem[];
  groups: NotificationGroup[];
  page: number;
  totalPages: number;
  totalUnread: number | null;
  isLoading: boolean;
  isLoadingMore: boolean;
  isMutating: boolean;
  error: string | null;
  actionError: string | null;
  preferences: NotificationPreference[];
  preferenceValues: Record<string, boolean>;
  isPreferencesLoading: boolean;
  preferenceError: string | null;
  updatingPreferenceId: string | null;
};

const INITIAL_STATE: NotificationsState = {
  items: [],
  groups: [],
  page: 0,
  totalPages: 0,
  totalUnread: null,
  isLoading: false,
  isLoadingMore: false,
  isMutating: false,
  error: null,
  actionError: null,
  preferences: [],
  preferenceValues: {},
  isPreferencesLoading: false,
  preferenceError: null,
  updatingPreferenceId: null,
};

function getErrorMessage(error: unknown) {
  return error instanceof Error
    ? error.message
    : 'Không thể tải thông báo. Vui lòng thử lại.';
}

export function useHeaderNotifications(
  isLoggedIn: boolean,
  userId?: string
): HeaderNotificationSource | undefined {
  const { jobSeekerApi } = useShell();
  const enabled = isLoggedIn && Boolean(jobSeekerApi);
  const [state, setState] = useState<NotificationsState>(INITIAL_STATE);
  const requestRef = useRef<AbortController | null>(null);
  const unreadCountRequestRef = useRef<AbortController | null>(null);
  const mutationControllerRef = useRef<AbortController | null>(null);
  const preferencesRequestRef = useRef<AbortController | null>(null);
  const preferenceMutationControllerRef = useRef<AbortController | null>(null);
  const failedPageRef = useRef<number | null>(null);
  const mutationRef = useRef(false);
  const preferenceMutationRef = useRef(false);
  const panelOpenRef = useRef(false);
  const realtimeSchedulerRef = useRef<ReturnType<
    typeof createNotificationUpdateScheduler
  > | null>(null);

  const scheduleRealtimeSync = useCallback(() => {
    realtimeSchedulerRef.current?.request();
  }, []);

  const loadUnreadCount = useCallback(async () => {
    if (!enabled || !jobSeekerApi || mutationRef.current) return;

    unreadCountRequestRef.current?.abort();
    const controller = new AbortController();

    unreadCountRequestRef.current = controller;

    try {
      const response = await getNotificationUnreadCount(
        jobSeekerApi,
        controller.signal
      );

      if (controller.signal.aborted || response.status !== 'success') return;

      const totalUnread = readNotificationUnreadCount(response);

      if (totalUnread !== null) {
        setState(current =>
          current.totalUnread === totalUnread
            ? current
            : { ...current, totalUnread }
        );
      }
    } catch {
      // The notifications list still provides totalUnread when the panel opens.
    } finally {
      if (unreadCountRequestRef.current === controller) {
        unreadCountRequestRef.current = null;
        realtimeSchedulerRef.current?.retryPending();
      }
    }
  }, [enabled, jobSeekerApi]);

  const loadPage = useCallback(
    async (page: number, background = false) => {
      if (!enabled || !jobSeekerApi || requestRef.current) return;

      const controller = new AbortController();

      requestRef.current = controller;
      if (!background) {
        setState(current => ({
          ...current,
          isLoading: page === 1,
          isLoadingMore: page > 1,
          error: null,
        }));
      }

      try {
        const response = await getNotifications(
          jobSeekerApi,
          page,
          NOTIFICATIONS_PAGE_SIZE,
          controller.signal
        );

        if (controller.signal.aborted) return;
        if (response.status !== 'success') {
          throw new Error(response.message || 'Không thể tải thông báo.');
        }

        const items = getNotificationItems(response).map(item =>
          normalizeNotification(item, response)
        );

        if (!background) failedPageRef.current = null;
        setState(current => {
          const existing = page === 1 ? [] : current.items;
          const ids = new Set(existing.map(item => item.id));
          const refreshed = background
            ? mergeNotificationRefresh(
                current.items,
                items,
                current.page,
                NOTIFICATIONS_PAGE_SIZE,
                response.meta.total
              )
            : null;

          return {
            ...current,
            items: refreshed?.items ?? [
              ...existing,
              ...items.filter(item => !ids.has(item.id)),
            ],
            groups: response.meta.groups,
            page: refreshed?.page ?? response.meta.page,
            totalPages: response.meta.totalPages,
            totalUnread: response.meta.totalUnread,
            error: background ? current.error : null,
          };
        });
      } catch (error) {
        if (!controller.signal.aborted && !background) {
          failedPageRef.current = page;
          setState(current => ({ ...current, error: getErrorMessage(error) }));
        }
      } finally {
        if (requestRef.current === controller) {
          requestRef.current = null;
          if (!background) {
            setState(current => ({
              ...current,
              isLoading: false,
              isLoadingMore: false,
            }));
          }
          realtimeSchedulerRef.current?.retryPending();
        }
      }
    },
    [enabled, jobSeekerApi]
  );

  const flushRealtimeSync = useCallback(() => {
    if (!enabled) return true;
    if (
      document.visibilityState !== 'visible' ||
      !navigator.onLine ||
      mutationRef.current ||
      requestRef.current ||
      unreadCountRequestRef.current
    ) {
      return false;
    }

    if (panelOpenRef.current) void loadPage(1, true);
    else void loadUnreadCount();

    return true;
  }, [enabled, loadUnreadCount, loadPage]);

  useEffect(() => {
    const scheduler = createNotificationUpdateScheduler(flushRealtimeSync);

    realtimeSchedulerRef.current = scheduler;

    return () => {
      scheduler.dispose();
      if (realtimeSchedulerRef.current === scheduler) {
        realtimeSchedulerRef.current = null;
      }
    };
  }, [flushRealtimeSync]);

  useNotificationStream(enabled, userId, scheduleRealtimeSync);

  const onOpen = useCallback(() => {
    panelOpenRef.current = true;
    void loadPage(1);
  }, [loadPage]);

  const onClose = useCallback(() => {
    panelOpenRef.current = false;
    requestRef.current?.abort();
    requestRef.current = null;
    preferencesRequestRef.current?.abort();
    preferencesRequestRef.current = null;
    preferenceMutationControllerRef.current?.abort();
    preferenceMutationControllerRef.current = null;
    preferenceMutationRef.current = false;
    setState(current => ({
      ...current,
      isLoading: false,
      isLoadingMore: false,
      isPreferencesLoading: false,
      updatingPreferenceId: null,
    }));
  }, []);

  const onLoadMore = useCallback(() => {
    if (mutationRef.current || state.error || state.page >= state.totalPages)
      return;

    void loadPage(state.page + 1);
  }, [loadPage, state.error, state.page, state.totalPages]);

  const onRetry = useCallback(() => {
    void loadPage(failedPageRef.current ?? 1);
  }, [loadPage]);

  const loadPreferences = useCallback(async () => {
    if (!enabled || !jobSeekerApi) return;

    preferencesRequestRef.current?.abort();
    const controller = new AbortController();

    preferencesRequestRef.current = controller;
    setState(current => ({
      ...current,
      isPreferencesLoading: true,
      preferenceError: null,
    }));

    try {
      const response = await getNotificationSettings(
        jobSeekerApi,
        controller.signal
      );

      if (controller.signal.aborted) return;
      if (response.status !== 'success') {
        throw new Error(response.message || 'Không thể tải cài đặt thông báo.');
      }

      setState(current => ({
        ...current,
        preferences: response.data.map(setting => ({
          id: setting.groupTemplateKey,
          label: setting.title,
          description: setting.description,
        })),
        preferenceValues: Object.fromEntries(
          response.data.map(setting => [
            setting.groupTemplateKey,
            setting.isEnabled,
          ])
        ),
        preferenceError: null,
      }));
    } catch (error) {
      if (!controller.signal.aborted) {
        setState(current => ({
          ...current,
          preferenceError: getErrorMessage(error),
        }));
      }
    } finally {
      if (preferencesRequestRef.current === controller) {
        preferencesRequestRef.current = null;
        setState(current => ({ ...current, isPreferencesLoading: false }));
      }
    }
  }, [enabled, jobSeekerApi]);

  const updatePreference = useCallback(
    async (id: string, value: boolean) => {
      if (!enabled || !jobSeekerApi || preferenceMutationRef.current) return;

      const controller = new AbortController();

      preferenceMutationRef.current = true;
      preferenceMutationControllerRef.current = controller;
      setState(current => ({
        ...current,
        updatingPreferenceId: id,
        preferenceError: null,
        preferenceValues: {
          ...current.preferenceValues,
          [id]: value,
        },
      }));

      try {
        const response = await updateNotificationSetting(
          jobSeekerApi,
          { groupTemplateKey: id, isEnabled: value },
          controller.signal
        );

        if (controller.signal.aborted) return;
        if (response && response.status !== 'success') {
          throw new Error(
            response.message || 'Không thể cập nhật cài đặt thông báo.'
          );
        }

        toast.success('Cập nhật cài đặt thông báo thành công');
      } catch (error) {
        if (!controller.signal.aborted) {
          setState(current => ({
            ...current,
            preferenceError: getErrorMessage(error),
            preferenceValues: {
              ...current.preferenceValues,
              [id]: !value,
            },
          }));
        }
      } finally {
        if (preferenceMutationControllerRef.current === controller) {
          preferenceMutationControllerRef.current = null;
          preferenceMutationRef.current = false;
          setState(current => ({
            ...current,
            updatingPreferenceId: null,
          }));
        }
      }
    },
    [enabled, jobSeekerApi]
  );

  const changeReadStatus = useCallback(
    async (ids: number[], unread: boolean) => {
      if (!jobSeekerApi || mutationRef.current) return;

      requestRef.current?.abort();
      requestRef.current = null;
      unreadCountRequestRef.current?.abort();
      unreadCountRequestRef.current = null;
      const controller = new AbortController();

      mutationRef.current = true;
      mutationControllerRef.current = controller;
      setState(current => ({
        ...current,
        isLoading: false,
        isLoadingMore: false,
        isMutating: true,
        actionError: null,
      }));

      try {
        const response = unread
          ? await markNotificationsUnread(jobSeekerApi, ids, controller.signal)
          : await markNotificationsRead(jobSeekerApi, ids, controller.signal);

        if (controller.signal.aborted) return;
        if (response && response.status !== 'success') {
          throw new Error(response.message || 'Không thể cập nhật thông báo.');
        }

        setState(current => {
          const targetIds = new Set(ids);
          const changed = current.items.filter(
            item =>
              (ids.length === 0 || targetIds.has(item.id)) &&
              item.unread !== unread
          ).length;
          const currentTotalUnread =
            current.totalUnread ??
            current.items.filter(item => item.unread).length;

          return {
            ...current,
            items: current.items.map(item =>
              ids.length === 0 || targetIds.has(item.id)
                ? { ...item, unread }
                : item
            ),
            totalUnread:
              ids.length === 0 && !unread
                ? 0
                : Math.max(
                    0,
                    currentTotalUnread + (unread ? changed : -changed)
                  ),
          };
        });
      } catch (error) {
        if (!controller.signal.aborted) {
          setState(current => ({
            ...current,
            actionError: getErrorMessage(error),
          }));
        }
      } finally {
        if (mutationControllerRef.current === controller) {
          mutationControllerRef.current = null;
          mutationRef.current = false;
          setState(current => ({ ...current, isMutating: false }));
          if (!controller.signal.aborted) void loadUnreadCount();
          realtimeSchedulerRef.current?.retryPending();
        }
      }
    },
    [jobSeekerApi, loadUnreadCount]
  );

  useEffect(() => {
    if (!enabled) return;

    void loadUnreadCount();

    return () => {
      unreadCountRequestRef.current?.abort();
      unreadCountRequestRef.current = null;
    };
  }, [enabled, loadUnreadCount]);

  useEffect(() => {
    if (enabled) return;

    requestRef.current?.abort();
    requestRef.current = null;
    unreadCountRequestRef.current?.abort();
    unreadCountRequestRef.current = null;
    mutationControllerRef.current?.abort();
    mutationControllerRef.current = null;
    preferencesRequestRef.current?.abort();
    preferencesRequestRef.current = null;
    preferenceMutationControllerRef.current?.abort();
    preferenceMutationControllerRef.current = null;
    mutationRef.current = false;
    preferenceMutationRef.current = false;
    panelOpenRef.current = false;
    failedPageRef.current = null;
    let active = true;

    queueMicrotask(() => {
      if (active) setState(INITIAL_STATE);
    });

    return () => {
      active = false;
    };
  }, [enabled]);

  useEffect(
    () => () => {
      requestRef.current?.abort();
      requestRef.current = null;
      unreadCountRequestRef.current?.abort();
      unreadCountRequestRef.current = null;
      mutationControllerRef.current?.abort();
      mutationControllerRef.current = null;
      preferencesRequestRef.current?.abort();
      preferencesRequestRef.current = null;
      preferenceMutationControllerRef.current?.abort();
      preferenceMutationControllerRef.current = null;
    },
    []
  );

  if (!jobSeekerApi) return undefined;

  return {
    items: state.items,
    groups: state.groups,
    totalUnread: state.totalUnread,
    isLoading: state.isLoading,
    isLoadingMore: state.isLoadingMore,
    isMutating: state.isMutating,
    error: state.error,
    actionError: state.actionError,
    preferences: state.preferences,
    preferenceValues: state.preferenceValues,
    isPreferencesLoading: state.isPreferencesLoading,
    preferenceError: state.preferenceError,
    updatingPreferenceId: state.updatingPreferenceId,
    hasMore: state.page < state.totalPages,
    onOpen,
    onClose,
    onLoadMore,
    onRetry,
    onMarkRead: (id: number) => changeReadStatus([id], false),
    onMarkUnread: (id: number) => changeReadStatus([id], true),
    onMarkAllRead: () => changeReadStatus([], false),
    onLoadPreferences: () => void loadPreferences(),
    onUpdatePreference: (id: string, value: boolean) =>
      void updatePreference(id, value),
  };
}
